import type { EpgNowNext } from '@lumos/contracts';

/**
 * «Сейчас в эфире» — ответ на МОМЕНТ ВРЕМЕНИ, а не факт, полученный однажды.
 *
 * Это и было сломано: список каналов спрашивал телепрограмму один раз при
 * открытии и больше к ней не возвращался, поэтому кончившаяся передача висела на
 * карточке весь вечер. В плеере то же самое случалось всякий раз, когда
 * запланированное пробуждение не срабатывало вовремя — свёрнутая вкладка,
 * уснувший телевизор, задержавшийся ответ.
 *
 * Лечится это не более частым опросом, а тем, что показанное СЧИТАЕТСЯ по
 * часам. У нас уже лежит «далее», и в момент её начала она становится «сейчас» —
 * без сети, мгновенно и точно. Сеть после этого нужна лишь чтобы узнать
 * следующую за ней, и спросить её можно спокойно, в фоне.
 */

/**
 * Что показывать в этот момент по тому, что известно.
 *
 * Кончившуюся передачу не оставляем «текущей» ни на секунду: подпись «сейчас» на
 * том, что уже прошло, — это ровно та ложь, из-за которой всё и затевалось.
 * Между передачами (в расписании бывают дыры) честнее показать пусто.
 */
export function advanceNowNext(entry: EpgNowNext | null, now: number): EpgNowNext | null {
  if (entry === null) return null;
  if (entry.now !== null && now < entry.now.stop) return entry;
  const next = entry.next;
  if (next !== null && next.start <= now && now < next.stop) {
    // «Далее» началась. Что идёт после неё, мы пока не знаем — и не выдумываем.
    return { now: next, next: null };
  }
  if (entry.now === null) return entry;
  return { now: null, next };
}

/**
 * Пора ли перечитать телепрограмму канала.
 *
 * Не «прошло N минут», а «то, что лежит, больше не описывает происходящее»:
 * текущая передача кончилась. Заодно это верный момент узнать новую «далее» —
 * подставленная нами взамен кончившейся оставляет её пустой.
 *
 * Пока «далее» ещё не началась (дыра в расписании), спрашивать нечего: сервер
 * ответит тем же самым, и опрос был бы холостым.
 */
export function nowNextStale(entry: EpgNowNext | null, now: number): boolean {
  if (entry === null) return false;
  if (entry.now !== null && now >= entry.now.stop) return true;
  return entry.now === null && entry.next !== null && now >= entry.next.stop;
}
